import React, { useState } from "react";

import {
  CancelOutlined,
  CheckCircleOutlined,
  Circle,
  ExpandLess,
  ExpandMore,
  MoreVert,
  OpenInNewOutlined,
  PersonRemoveOutlined,
  SendOutlined,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Link,
  Menu,
  Checkbox,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { enqueueSnackbar } from "notistack";
import { connect } from "react-redux";
import { NavLink, useParams } from "react-router-dom";

import { Tooltip } from "../../../../common";
import {
  deleteResume as deleteResumeAction,
  rejectResume as rejectResumeAction,
} from "../../../../redux/slices/resumeSlice";
import { resume_stages } from "../../../../static/data";
import { getCamelCaseWord, getStageColor } from "../../../../utils/functions";

/* =============================================================================
<Row />
============================================================================= */
const Row = ({
  page,
  isRowExpended,
  onExpand,
  resume,
  selectedRows,
  deleteResume,
  rejectResume,
  setSelectedRows,
  onRequestAssesment,
}) => {
  const { postId } = useParams();
  const [menuLoading, setMenuLoding] = useState(false);
  const stage = resume?.stage?.toLowerCase();
  const showRowSelectCheckbox =
    selectedRows === "all" || selectedRows?.length > 0 ? true : false;
  const isRowSelected =
    Array.isArray(selectedRows) && selectedRows?.find((r) => r === resume?.id); // wil be id;

  const handleRowSelect = () => {
    if (isRowSelected) {
      setSelectedRows((prev) => prev?.filter((r) => r !== resume?.id));
    } else if (selectedRows === "all") {
      setSelectedRows([resume?.id]);
    } else {
      setSelectedRows((prev) => [...prev, resume?.id]);
    }
  };

  const handleDelete = async () => {
    if (menuLoading == "delete") return;
    try {
      setMenuLoding("delete");

      await deleteResume({ ids: [resume?.id], page });
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setMenuLoding(false);
    }
  };

  const handleReject = async () => {
    if (menuLoading === "reject") return;
    try {
      setMenuLoding("reject");

      await rejectResume({ ids: [resume?.id] });
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setMenuLoding(false);
    }
  };

  return (
    <TableRow hover role="checkbox" tabIndex={-1}>
      <TableCell align="left" sx={{ border: "none" }}>
        {showRowSelectCheckbox ? (
          <Checkbox
            checked={isRowSelected || selectedRows === "all"}
            onChange={handleRowSelect}
          />
        ) : (
          <IconButton onClick={onExpand}>
            {!isRowExpended ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        )}
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        {resume?.processing_status === "processing" ||
        resume?.stage === "unprocessed" ? (
          <Tooltip
            title={
              resume?.processing_status === "processing"
                ? "CV is in processing"
                : "Failed to processed"
            }
          >
            <Typography color="#999" fontSize="14px" fontWeight={600}>
              {resume?.candidate_name}
            </Typography>
          </Tooltip>
        ) : (
          <Link
            component={NavLink}
            to={`/resume-screening-candidates/${postId}/candidate-result/${resume?.id}`}
            fontSize="14px"
            fontWeight={600}
            target="_blank"
            rel="noopener noreferrer"
          >
            {resume?.candidate_name}
          </Link>
        )}
      </TableCell>
      {/* <TableCell align="left" sx={{ border: "none" }}>
        <Box display="flex">
          <Box
            sx={{
              bgcolor: !resume?.overall_score ? "#75DA1C" : "purple.main",
              borderRadius: "30px",
              px: 2,
              py: 1,
            }}
          >
            <Typography color="white" fontSize="13px" fontWeight={600}>
              {resume?.overall_score ? `Score: ${resume?.overall_score}%` : 'Generate'}
            </Typography>
          </Box>
        </Box>
      </TableCell> */}

      <TableCell align="left" sx={{ border: "none" }}>
        {resume?.processing_status === "processing" ? (
          <Typography color="#999" fontSize="14px" fontWeight={600}>
            Processing
          </Typography>
        ) : (
          <Box display="flex">
            <Box
              sx={{
                bgcolor: !resume?.resume_passed ? "#FF5B5B" : "#8B5CF6",
                borderRadius: "30px",
                px: 2,
                py: 1,
              }}
            >
              <Typography color="white" fontSize="13px" fontWeight={600}>
                {resume?.resume_passed ? "Passed" : "Not Passed"}{" "}
                {resume?.resume_score || 0}%
              </Typography>
            </Box>
          </Box>
        )}
      </TableCell>

      <TableCell align="center" sx={{ border: "none" }}>
        <Box display="flex">
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              bgcolor: getStageColor(stage),
              borderRadius: "30px",
              px: 2,
              py: 1,
            }}
          >
            <Typography color="white" fontSize="13px" fontWeight={600}>
              {getCamelCaseWord(stage)}
            </Typography>
            <Tooltip
              title={
                <Box>
                  {resume_stages.map((s, index) => {
                    const currentStageIndex = resume_stages.findIndex(
                      (_stag) => _stag.toLowerCase() === stage.toLowerCase()
                    );

                    const isChecked = index <= currentStageIndex;

                    if (s === "Rejected" && resume?.stage !== "Rejected") {
                      return null;
                    }

                    return (
                      <Box
                        key={s}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1.5}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Circle
                            sx={{
                              width: "10px",
                              height: "10px",
                              color: getStageColor(s),
                            }}
                          />
                          <Typography
                            color={getStageColor(s)}
                            fontSize="17px"
                            fontWeight={500}
                            mr={2}
                          >
                            {s}
                          </Typography>
                        </Box>
                        {isChecked ? (
                          <CheckCircleOutlined
                            sx={{
                              color: "#80D63E",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        ) : (
                          <CancelOutlined
                            sx={{
                              color: "#F24B4B",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              }
            >
              <Circle sx={{ color: "white", width: "5px", height: "5px" }} />
            </Tooltip>
          </Box>
        </Box>
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        <Typography fontSize="13px" fontWeight={500}>
          <Typography fontSize="13px" fontWeight={500}>
            {dayjs(resume?.created_at).format("DD/MM/YYYY")}
          </Typography>
        </Typography>
      </TableCell>
      <TableCell align="left" sx={{ border: "none" }}>
        <Box display="flex" gap={1}>
          {resume?.processing_status !== "processing" &&
            resume?.stage !== "unprocessed" && (
              <Link
                component={NavLink}
                to={`/resume-screening-candidates/${postId}/candidate-result/${resume?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                mt={0.5}
              >
                <OpenInNewOutlined
                  sx={{ width: "22px", height: "22px", color: "#0891B2" }}
                />
              </Link>
            )}

          <PopupState variant="popover" popupId="Rowdldsf">
            {(popupState) => (
              <React.Fragment>
                <Box>
                  <IconButton sx={{ p: 0.2 }} {...bindTrigger(popupState)}>
                    <MoreVert />
                  </IconButton>
                </Box>
                <Menu {...bindMenu(popupState)}>
                  {resume?.stage !== "Invited" ? (
                    <MenuItem
                      sx={{ py: 1, gap: 1 }}
                      onClick={() => {
                        onRequestAssesment([resume?.id]); // replace with candidate ID
                        // onRequestAssesment(true); // this will open select lang modal
                        popupState.close();
                      }}
                    >
                      <SendOutlined sx={{ width: "18px", height: "18px" }} />
                      <Typography
                        fontFamily="Outfit"
                        fontWeight={500}
                        fontSize="12px"
                      >
                        Send Interview test
                      </Typography>
                    </MenuItem>
                  ) : null}
                  {/* <MenuItem sx={{ py: 1, gap: 1 }}>
                  <MailOutline sx={{ width: "18px", height: "18px" }} />
                  <Typography
                    fontFamily="Outfit"
                    fontWeight={500}
                    fontSize="12px"
                  >
                    Send results
                  </Typography>
                </MenuItem> */}
                  <Tooltip title="Coming Soon" arrow placement="right">
                    <span style={{ display: "flex" }}>
                      <MenuItem sx={{ py: 1, gap: 1 }} disabled>
                        <PersonRemoveOutlined
                          sx={{ width: "18px", height: "18px" }}
                        />
                        {menuLoading === "reject" ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Typography
                            fontFamily="Outfit"
                            fontWeight={500}
                            fontSize="12px"
                          >
                            Reject
                          </Typography>
                        )}
                      </MenuItem>
                    </span>
                  </Tooltip>

                  {/* <MenuItem sx={{ py: 1, gap: 1 }} onClick={handleDelete}>
                    <DeleteOutline
                      sx={{ color: "red", width: "18px", height: "18px" }}
                    />
                    {menuLoading === "delete" ? (
                      <CircularProgress size={18} sx={{ color: "red" }} />
                    ) : (
                      <Typography
                        color="red"
                        fontFamily="Outfit"
                        fontWeight={500}
                        fontSize="12px"
                      >
                        Delete candidate
                      </Typography>
                    )}
                  </MenuItem> */}
                </Menu>
              </React.Fragment>
            )}
          </PopupState>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const mapDipatchToProps = {
  deleteResume: deleteResumeAction,
  rejectResume: rejectResumeAction,
};

/* Export
============================================================================= */
export default connect(null, mapDipatchToProps)(React.memo(Row));
